/* @flow */
// Plays the player's queue through the phone app's native audio engine instead
// of an <audio> tag, so music, ads and announcements keep going with the screen
// locked and show on the lock screen. Used only inside the GymRadio app.
//
// The web player stays in charge of WHAT plays (stations, ads, announcements,
// free plan, skips). We hand the engine the current track plus everything
// already lined up, so it can carry on by itself while the phone is locked.
// When the engine moves on, we catch the web queue up to match.
import React, { PureComponent } from 'react'
import { reaction } from 'mobx'
import { nativeAudio, appLog } from '../../helpers/nativeApp'

const QUEUE_AHEAD = 25

// The lock screen loads the picture itself, so it needs a full web address
const absolute = (url) => {
  if (!url) return ''
  try { return new URL(url, window.location.href).href } catch (e) { return '' }
}

const itemsFrom = (tracks, rawArtwork) => {
  const artwork = absolute(rawArtwork)
  const items = []
  for (const t of tracks) {
    if (!t || !t.url) break // keep the order: stop at the first track not loaded yet
    items.push({
      id: t.id || t.uuid || t.url,
      url: absolute(t.url), // promos are stored as /audio/... on this site
      title: t.title || '',
      artist: t.artist || '',
      artwork: artwork || '',
      gaiaId: t.gaiaId || '',
      uuid: t.uuid || ''
    })
    if (items.length >= QUEUE_AHEAD) break
  }
  return items
}

class NativeAudio extends PureComponent<*> {
  engine = nativeAudio()
  listeners = []
  position = 0
  duration = 0
  volume = 1
  jobs = Promise.resolve()
  playing = false

  // Same shape the player uses for the <audio> element
  audioEl = (() => {
    const self = this
    return {
      play () { self.playing = true; return self.engine.play().catch(() => {}) },
      pause () { self.playing = false; return self.engine.pause().catch(() => {}) },
      load () {},
      get currentTime () { return self.position },
      set currentTime (v) {},
      get duration () { return self.duration },
      get volume () { return self.volume },
      set volume (v) {
        self.volume = v
        self.engine.setVolume({ volume: v }).catch(() => {})
      }
    }
  })()

  componentDidMount () {
    const e = this.engine
    const add = (name, fn) => {
      const l = e.addListener(name, fn)
      // addListener returns a handle (or a promise of one in newer Capacitor)
      this.listeners.push(l)
    }
    const log = (...a) => appLog('engine', ...a)
    add('play', ({ id }) => this.enqueue(async () => {
      log('play', id)
      await this.catchUpTo(id)
      this.props.onPlay && this.props.onPlay()
    }))
    add('pause', () => { log('pause'); this.props.onPause && this.props.onPause() })
    add('queueEnded', ({ id }) => this.enqueue(async () => {
      log('queueEnded', id)
      // Nothing left in the engine: move the web queue on, which sends more
      await this.catchUpTo(id)
      if (this.headId() === id && this.props.onAdvance) await this.props.onAdvance(1)
    }))
    add('position', ({ position, duration }) => {
      this.position = position || 0
      this.duration = duration || 0
    })
    add('error', ({ id, status, message }) => this.enqueue(async () => {
      log('error', id, status, message)
      await this.catchUpTo(id)
      if (this.headId() === id && this.props.onError) this.props.onError(status || 0, message || '')
    }))

    this.disposeQueue = reaction(
      () => {
        const { playlistStore, artwork } = this.props
        return JSON.stringify(itemsFrom(playlistStore.tracks, artwork))
      },
      () => this.sendQueue(),
      { delay: 50 }
    )
    // If the engine is still playing from before (app reopened), carry on
    Promise.resolve(this.engine.getPosition ? this.engine.getPosition() : null)
      .then((r) => { if (r && r.playing) this.playing = true })
      .catch(() => {})
      .then(() => this.sendQueue(!!this.props.autoPlay))
  }

  componentWillUnmount () {
    this.disposeQueue && this.disposeQueue()
    this.listeners.forEach((l) => {
      Promise.resolve(l).then((h) => h && h.remove && h.remove()).catch(() => {})
    })
    this.engine.pause().catch(() => {})
  }

  headId () {
    const t = this.props.playlistStore.tracks[0]
    return t ? (t.id || t.uuid || t.url) : ''
  }

  enqueue (job) {
    this.jobs = this.jobs.then(job).catch(() => {})
    return this.jobs
  }

  // The engine has moved on to `id`: drop the tracks it already played
  async catchUpTo (id) {
    const ids = this.props.playlistStore.tracks.map((t) => t.id || t.uuid || t.url)
    const k = ids.indexOf(id)
    if (k > 0 && this.props.onAdvance) {
      await this.props.onAdvance(k)
    }
  }

  // Skip button: start the (new) first track now, whatever is playing
  skipNow () {
    this.sendQueue(false, true)
  }

  sendQueue (autoPlay = false, restart = false) {
    const { playlistStore, artwork } = this.props
    const items = itemsFrom(playlistStore.tracks, artwork)
    if (!items.length) return
    appLog('send queue', items.length, 'first:', items[0].artist, '-', items[0].title, 'play:', autoPlay || this.playing)
    const background = this.props.background
      ? { ...this.props.background, artwork: absolute(this.props.background.artwork) }
      : null
    this.engine.setQueue({ items, autoPlay: autoPlay || this.playing, background, restart }).catch(() => {})
    if (autoPlay) this.playing = true
  }

  render () {
    return null
  }
}

export default NativeAudio
